import { useParams } from 'react-router-dom';

import { LocalTime } from '~/components/common/LocalTime';
import { Select, ToggleField } from '~/components/form';
import { RecordPageShell } from '~/components/layout';
import {
  Alert,
  Badge,
  Card,
  CardBody,
  CardFooter,
  CardSection,
  FormActions,
  StatusChip,
} from '~/components/ui';
import { useRecordDraft } from '~/hooks/useRecordDraft';
import { useRecordSave } from '~/hooks/useRecordSave';
import { useUnsavedGuard } from '~/hooks/useUnsavedGuard';
import { useUrlParams } from '~/hooks/useUrlParams';
import { isNotFound } from '~/lib/apiError';
import { queryErrorMessage } from '~/modules/query';
import { useGetRequestQuery, useModerateRequestMutation } from '~/modules/query/featureFlags';

import {
  REQUEST_STATUSES,
  REQUEST_STATUS_LABELS,
  REQUEST_STATUS_TONES,
} from '~/components/Constants/featureFlagsConstants';
import { toOptions } from '~/components/pages/feature-flags/common';
import { useReference } from '~/components/pages/feature-flags/useReference';
import type { FeatureRequestDetail, RequestStatus } from '~/components/pages/feature-flags/types';
import { routes } from '~/app/routes';

interface Draft {
  status: RequestStatus;
  hidden: boolean;
}

const toDraft = (request: FeatureRequestDetail): Draft => ({
  status: request.status,
  hidden: request.hidden,
});

const FORM_ID = 'request-form';

/** One feature request (`/feature-flags/requests/:id?product=`): the request details plus the moderation form. There is no `/new`: customers submit requests from the product. */
export default function RequestPage() {
  const { id } = useParams<{ id: string }>();
  const requestId = id ?? '';
  const url = useUrlParams();
  const ref = useReference();
  const { products } = ref.reference;
  // The product is part of the request's identity; the list's default (the first product) applies when the link has none.
  const product = products.find((p) => p.key === url.get('product'))?.key ?? products[0]?.key ?? '';
  const productName = products.find((p) => p.key === product)?.name ?? product;
  const list = `${routes.featureFlagsSection('requests')}${product ? `?product=${encodeURIComponent(product)}` : ''}`;

  const query = useGetRequestQuery({ id: requestId, product }, { skip: !ref.loaded || !product });
  const request = query.currentData;
  const { draft, patch, dirty, reset, markSaved } = useRecordDraft<FeatureRequestDetail, Draft>(
    request,
    { id: `${product}:${requestId}`, version: request?.version, toDraft },
  );
  const guard = useUnsavedGuard(dirty);

  const [moderateRequest] = useModerateRequestMutation();
  const save = useRecordSave<Draft>({
    save: async (d) => {
      if (request)
        await moderateRequest({
          id: request.id,
          data: { product, version: request.version, status: d.status, hidden: d.hidden },
        }).unwrap();
    },
    success: 'Feature request saved',
    failure: 'The feature request could not be saved. Try again.',
  });

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (draft && (await save.submit(draft))) markSaved(draft);
  };

  const title = request?.title ?? 'Feature request';
  const notFound = ref.loaded && (!product || isNotFound(query.error));
  const loadError = ref.loaded ? null : ref.error;
  const failed = (query.isError && !notFound) || !!loadError;

  return (
    <RecordPageShell
      title={title}
      breadcrumbs={[
        { label: 'Feature flags', href: routes.featureFlags },
        { label: 'Feature requests', href: list },
        { label: title },
      ]}
      loading={!request}
      notFound={notFound}
      notFoundTitle="Feature request not found"
      backTo={list}
      backLabel="Back to feature requests"
      error={
        failed
          ? { what: 'the feature request', message: queryErrorMessage(query.error ?? loadError) }
          : null
      }
      onRetry={() => {
        if (query.isError) query.refetch();
        ref.refetch();
      }}
      guard={guard}
      testId="request"
    >
      {request && draft && (
        <>
          <Card>
            <CardBody className="space-y-6">
              <CardSection title="Request" description={productName}>
                <div className="space-y-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <StatusChip
                      label={REQUEST_STATUS_LABELS[request.status]}
                      tone={REQUEST_STATUS_TONES[request.status]}
                      dot
                    />
                    {request.hidden ? (
                      <Badge size="sm" color="warning">
                        Hidden from users
                      </Badge>
                    ) : (
                      !request.approvedAt && (
                        <Badge size="sm" color="gray">
                          Awaiting approval
                        </Badge>
                      )
                    )}
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      Submitted <LocalTime value={request.createdAt} />
                    </span>
                  </div>
                  <p className="whitespace-pre-wrap text-sm text-gray-800 dark:text-white/90">
                    {request.description}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {request.voteCount} {request.voteCount === 1 ? 'vote' : 'votes'} •{' '}
                    {request.enhancementCount}{' '}
                    {request.enhancementCount === 1 ? 'enhancement' : 'enhancements'}
                  </p>
                </div>
              </CardSection>

              <CardSection title={`Enhancements (${request.enhancements.length})`}>
                {request.enhancements.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No enhancements yet. Ideas added by your users appear here.
                  </p>
                ) : (
                  <ul className="divide-y divide-gray-100 dark:divide-white/[0.04]">
                    {request.enhancements.map((e) => (
                      <li key={e.id} className="space-y-1 py-3">
                        <div className="flex items-center justify-between gap-3 text-sm">
                          <span className="font-medium text-gray-800 dark:text-white/90">
                            {e.authorName}
                          </span>
                          <LocalTime
                            value={e.createdAt}
                            className="text-gray-500 dark:text-gray-400"
                          />
                        </div>
                        <p className="whitespace-pre-wrap text-sm text-gray-800 dark:text-white/90">
                          {e.description}
                        </p>
                      </li>
                    ))}
                  </ul>
                )}
              </CardSection>
            </CardBody>
          </Card>

          <form id={FORM_ID} noValidate onSubmit={submit}>
            <Card>
              <CardBody className="space-y-6">
                {save.error && <Alert variant="error" message={save.error} />}
                <CardSection title="Manage request">
                  <div className="space-y-5">
                    <Select
                      id="request-status"
                      label="Status"
                      value={draft.status}
                      options={toOptions(
                        REQUEST_STATUSES,
                        (s) => REQUEST_STATUS_LABELS[s as RequestStatus],
                      )}
                      onChange={(status) => patch({ status: status as RequestStatus })}
                      helperText="Approving a suggestion makes it eligible for display to customers."
                    />
                    <ToggleField
                      id="request-hidden"
                      testId="request-hidden"
                      label="Visibility"
                      text={draft.hidden ? 'Hidden from users' : 'Visible to users'}
                      checked={draft.hidden}
                      onChange={(hidden) => patch({ hidden })}
                    />
                  </div>
                </CardSection>
              </CardBody>
              <CardFooter>
                <FormActions
                  form={FORM_ID}
                  onDiscard={() => {
                    reset();
                    save.clearError();
                  }}
                  disabled={!dirty}
                  disabledReason="No changes to save"
                  busy={save.busy}
                  saveTestId="request-save"
                />
              </CardFooter>
            </Card>
          </form>
        </>
      )}
    </RecordPageShell>
  );
}
